import { useEffect, useState, useRef, useCallback } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { BroadcastChannelNames, RelativityAuthMessageTypes, RelativityAuthMessages } from '@/types/broadcast-channels';

export function useRelativityAuth() {
  const authInfo = useAuthInfo();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Check auth status
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/status`, {
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check auth status');
      }

      const data = await response.json();
      setIsAuthenticated(data.isAuthenticated);
    } catch (error) {
      console.error('Error checking Relativity auth status:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [authInfo.tokens]);

  // Listen for OAuth callback
  useEffect(() => {
    const channel = new BroadcastChannel(BroadcastChannelNames.RelativityAuth);
    broadcastChannelRef.current = channel;

    const handleBroadcastMessage = (event: MessageEvent<RelativityAuthMessages>) => {
      if (event.data.type === RelativityAuthMessageTypes.Success) {
        toast.success('Successfully connected to Relativity');
        setIsAuthenticated(true);
        setIsConnecting(false);
      } else if (event.data.type === RelativityAuthMessageTypes.Error) {
        toast.error(`Failed to connect to Relativity: ${event.data.description || event.data.error}`);
        setIsConnecting(false);
      }
    };

    channel.addEventListener('message', handleBroadcastMessage);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Connect to Relativity
  const connectRelativity = async () => {
    setIsConnecting(true);
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/start`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start OAuth flow');
      }

      const data = await response.json();
      window.open(data.authUrl, 'relativity-auth', 'width=800,height=600');
    } catch (error) {
      console.error('Error starting Relativity OAuth:', error);
      toast.error('Failed to start Relativity authentication');
      setIsConnecting(false);
    }
  };

  const disconnectRelativity = async () => {
    setIsDisconnecting(true);
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Relativity');
      }

      setIsAuthenticated(false);
      toast.success('Disconnected from Relativity');
    } catch (error) {
      console.error('Error disconnecting Relativity:', error);
      toast.error('Failed to disconnect Relativity');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return {
    isAuthenticated,
    isLoading,
    isConnecting,
    isDisconnecting,
    connectRelativity,
    disconnectRelativity,
    refreshAuthStatus: checkAuthStatus,
  };
}
